/**
 * Virtual Host Setup Wizard
 *
 * 5-step wizard for configuring Virtual Host service:
 * 1. Welcome - Introduction and overview
 * 2. Phone Number - Provision Twilio number
 * 3. Stripe Connect - Link payment account
 * 4. Preferences - Configure operating hours, greetings, features
 * 5. Finish - Review and activate
 *
 * Also serves as Settings editor after initial setup.
 */

import React, { useState, useEffect } from 'react';
import { X, ArrowRight, ArrowLeft, Phone, CreditCard, Settings, CheckCircle, Sparkles, Clock, MessageSquare, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { apiClient } from '@/services/api/apiClient';

interface VirtualHostSetupWizardProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'setup' | 'settings';
  onSetupComplete?: () => void;
}

type SetupStep = 1 | 2 | 3 | 4 | 5;

interface PhoneNumber {
  phone_number: string;
  friendly_name: string;
  monthly_cost: string;
}

interface Preferences {
  welcome_greeting: string;
  operating_hours: {
    [key: string]: { open: string; close: string };
  };
  working_days: string[];
  wait_time_minutes: number;
  human_contact_phone: string;
  human_contact_email: string;
  enable_reservations: boolean;
  enable_catering: boolean;
  enable_events: boolean;
}

const DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const VirtualHostSetupWizard: React.FC<VirtualHostSetupWizardProps> = ({
  isOpen,
  onClose,
  mode,
  onSetupComplete
}) => {
  const [currentStep, setCurrentStep] = useState<SetupStep>(1);
  const [loading, setLoading] = useState(false);

  // Phone Number State
  const [areaCode, setAreaCode] = useState('');
  const [searchingNumbers, setSearchingNumbers] = useState(false);
  const [availableNumbers, setAvailableNumbers] = useState<PhoneNumber[]>([]);
  const [selectedNumber, setSelectedNumber] = useState<string>('');
  const [provisioningNumber, setProvisioningNumber] = useState(false);

  // Stripe Connect State
  const [stripeAccountStatus, setStripeAccountStatus] = useState<'not_started' | 'pending' | 'verified'>('not_started');
  const [stripeOnboardingUrl, setStripeOnboardingUrl] = useState<string>('');
  const [checkingStripeStatus, setCheckingStripeStatus] = useState(false);

  // Preferences State
  const [preferences, setPreferences] = useState<Preferences>({
    welcome_greeting: 'Thank you for calling! How can I help you today?',
    operating_hours: {
      monday: { open: '09:00', close: '21:00' },
      tuesday: { open: '09:00', close: '21:00' },
      wednesday: { open: '09:00', close: '21:00' },
      thursday: { open: '09:00', close: '21:00' },
      friday: { open: '09:00', close: '21:00' },
      saturday: { open: '09:00', close: '21:00' },
      sunday: { open: '09:00', close: '21:00' }
    },
    working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
    wait_time_minutes: 15,
    human_contact_phone: '',
    human_contact_email: '',
    enable_reservations: false,
    enable_catering: false,
    enable_events: false
  });

  /**
   * Load existing preferences (for settings mode)
   * Also check for existing phone number in setup mode
   */
  useEffect(() => {
    if (isOpen && mode === 'settings') {
      loadExistingPreferences();
    }
    if (isOpen && mode === 'setup') {
      checkExistingPhoneNumber();
    }
  }, [isOpen, mode]);

  /**
   * Check if phone number already exists and skip Step 2 if so
   */
  const checkExistingPhoneNumber = async () => {
    try {
      const response = await apiClient.get('/virtual-host/setup/status');
      const data = response.data;

      console.log('[VirtualHostSetupWizard] Checking existing setup:', data);

      // If phone number already configured, skip to Step 3
      if (data.phone_number && data.phone_configured) {
        console.log('[VirtualHostSetupWizard] Phone already configured, skipping to Step 3');
        setSelectedNumber(data.phone_number);
        setCurrentStep(3); // Skip directly to Stripe Connect
        toast.info('Phone number already configured. Continuing to Stripe setup.');
      }
    } catch (error) {
      console.error('[VirtualHostSetupWizard] Error checking existing phone number:', error);
      // Don't block wizard if check fails - just start from Step 1
    }
  };

  const loadExistingPreferences = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/virtual-host/setup/status');
      const data = response.data;

      // Load phone number status
      if (data.phone_number) {
        setSelectedNumber(data.phone_number);
      }

      // Load Stripe status
      setStripeAccountStatus(data.stripe_status || 'not_started');

      // Load preferences
      if (data.preferences) {
        setPreferences({
          ...preferences,
          ...data.preferences
        });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      toast.error('Failed to load existing settings');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Step 2: Search for available phone numbers
   */
  const handleSearchNumbers = async () => {
    if (areaCode.length !== 3) {
      toast.error('Please enter a 3-digit area code');
      return;
    }

    try {
      setSearchingNumbers(true);
      const response = await apiClient.get(`/virtual-hostess/twilio/search-numbers?area_code=${areaCode}`);
      const numbers = response.data.numbers || [];
      const numberType = response.data.number_type;
      const fallbackMessage = response.data.fallback_message;

      setAvailableNumbers(numbers);

      if (numbers.length === 0) {
        // Show helpful message with suggestions
        const message = response.data.message || 'No numbers found for this area code. Try another (e.g., 512, 415, 646, 917, 720).';
        toast.error(message);
      } else if (numberType === 'toll_free' && fallbackMessage) {
        // Show toll-free fallback message with positive framing
        toast.success(`Found ${numbers.length} toll-free numbers`, {
          description: 'Forward your existing business phone to this toll-free number. Zero business interruption!',
          duration: 6000
        });
      } else {
        toast.success(`Found ${numbers.length} available numbers`);
      }
    } catch (error) {
      console.error('Error searching numbers:', error);
      toast.error('Failed to search phone numbers');
    } finally {
      setSearchingNumbers(false);
    }
  };

  /**
   * Step 2: Provision selected phone number
   */
  const handleProvisionNumber = async () => {
    console.log('[VirtualHostSetupWizard] handleProvisionNumber called with selectedNumber:', selectedNumber);

    if (!selectedNumber) {
      toast.error('Please select a phone number');
      return;
    }

    try {
      setProvisioningNumber(true);
      console.log('[VirtualHostSetupWizard] Making POST request to /virtual-hostess/twilio/provision-number');

      const response = await apiClient.post('/virtual-hostess/twilio/provision-number', {
        phone_number: selectedNumber
      });

      console.log('[VirtualHostSetupWizard] Provision response:', response.data);

      if (response.data.success) {
        toast.success('Phone number provisioned successfully!');
        setCurrentStep(3); // Move to Stripe Connect
      } else {
        console.error('[VirtualHostSetupWizard] Provision failed - success=false');
        toast.error('Failed to provision phone number');
      }
    } catch (error: any) {
      console.error('[VirtualHostSetupWizard] Error provisioning number:', error);
      console.error('[VirtualHostSetupWizard] Error response:', error.response);
      toast.error(error.response?.data?.message || 'Failed to provision phone number');
    } finally {
      setProvisioningNumber(false);
    }
  };

  /**
   * Step 3: Create Stripe Connected Account
   */
  const handleCreateStripeAccount = async () => {
    try {
      setLoading(true);
      console.log('🟢 [FRONTEND] Calling /stripe-connect/create-account...');

      // Send request with country parameter (required by backend)
      const response = await apiClient.post('/stripe-connect/create-account', {
        country: 'US'  // Default to US, backend will auto-populate email and restaurant_name
      });

      console.log('🟢 [FRONTEND] Response received:', response.data);
      const onboardingUrl = response.data.onboarding_url;

      if (onboardingUrl) {
        // Redirect to Stripe onboarding in same window
        toast.success('Redirecting to Stripe onboarding...');
        window.location.href = onboardingUrl;
      }
    } catch (error: any) {
      console.error('🔴 [FRONTEND] Error creating Stripe account:', {
        error: error,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      const errorMessage = error.response?.data?.detail || 'Failed to create Stripe account';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Step 3: Check Stripe account status
   */
  const handleCheckStripeStatus = async () => {
    try {
      setCheckingStripeStatus(true);
      const response = await apiClient.get('/stripe-connect/account-status');
      const status = response.data.status;

      setStripeAccountStatus(status);

      if (status === 'verified') {
        toast.success('Stripe account verified! You can now proceed.');
        setTimeout(() => setCurrentStep(4), 1500);
      } else if (status === 'pending') {
        toast.info('Stripe account pending verification. This may take 1-2 days.');
      } else {
        toast.warning('Please complete Stripe onboarding first.');
      }
    } catch (error) {
      console.error('Error checking Stripe status:', error);
      toast.error('Failed to check Stripe status');
    } finally {
      setCheckingStripeStatus(false);
    }
  };

  /**
   * Step 4: Save preferences
   */
  const handleSavePreferences = async () => {
    try {
      setLoading(true);
      await apiClient.post('/virtual-host/setup/set-preferences', preferences);
      toast.success('Preferences saved successfully!');
      setCurrentStep(5); // Move to finish
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
      setLoading(false);
    }
  };

  /**
   * Step 5: Finish setup and activate
   */
  const handleFinishSetup = async () => {
    try {
      setLoading(true);
      await apiClient.post('/virtual-host/setup/finish');
      toast.success('Virtual Host setup complete! Your AI is now active.');

      if (onSetupComplete) {
        onSetupComplete();
      }

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error finishing setup:', error);
      toast.error('Failed to complete setup');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle previous step
   */
  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as SetupStep);
    }
  };

  /**
   * Handle next step
   *
   * Step 2: Automatically provision phone number if selected
   * Step 3: Check Stripe verification before proceeding
   */
  const handleNextStep = async () => {
    console.log('[VirtualHostSetupWizard] handleNextStep called - currentStep:', currentStep);

    // Step 2: Auto-provision phone number when clicking Next
    if (currentStep === 2) {
      console.log('[VirtualHostSetupWizard] Step 2 detected - checking selectedNumber:', selectedNumber);

      if (!selectedNumber) {
        toast.error('Please select a phone number first');
        return;
      }

      // Automatically provision the selected number
      console.log('[VirtualHostSetupWizard] Calling handleProvisionNumber with:', selectedNumber);
      await handleProvisionNumber();
      return; // handleProvisionNumber will move to Step 3 on success
    }

    if (currentStep === 3 && stripeAccountStatus !== 'verified') {
      toast.error('Please complete Stripe verification first');
      return;
    }

    if (currentStep < 5) {
      console.log('[VirtualHostSetupWizard] Advancing to next step from:', currentStep);
      setCurrentStep((currentStep + 1) as SetupStep);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-6 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-8 h-8" />
            <h2 className="text-2xl font-bold">
              {mode === 'setup' ? 'Virtual Host Setup' : 'Virtual Host Settings'}
            </h2>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4, 5].map((step) => (
              <div key={step} className="flex items-center">
                <div
                  className={`
                    w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm
                    transition-all
                    ${currentStep >= step
                      ? 'bg-white text-purple-600'
                      : 'bg-white/20 text-white/60'
                    }
                  `}
                >
                  {step}
                </div>
                {step < 5 && (
                  <div
                    className={`
                      h-1 w-12 mx-2
                      ${currentStep > step ? 'bg-white' : 'bg-white/20'}
                    `}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto max-h-[calc(90vh-240px)]">
          <AnimatePresence mode="wait">
            {/* Step 1: Welcome */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Welcome to Virtual Host!</h3>
                <p className="text-gray-600 mb-6">
                  Let's set up your AI-powered phone ordering system in just a few steps.
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-purple-50 rounded-xl">
                    <Phone className="w-6 h-6 text-purple-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">1. Phone Number</h4>
                      <p className="text-sm text-gray-600">Get a dedicated Twilio number for your restaurant</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-pink-50 rounded-xl">
                    <CreditCard className="w-6 h-6 text-pink-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">2. Payment Setup</h4>
                      <p className="text-sm text-gray-600">Connect Stripe to receive payments directly</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-xl">
                    <Settings className="w-6 h-6 text-blue-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">3. Preferences</h4>
                      <p className="text-sm text-gray-600">Configure hours, greetings, and features</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-green-50 rounded-xl">
                    <CheckCircle className="w-6 h-6 text-green-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">4. Activate</h4>
                      <p className="text-sm text-gray-600">Go live and start receiving orders!</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Phone Number */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Provision Phone Number</h3>
                <p className="text-gray-600 mb-6">
                  Search for available phone numbers by area code.
                </p>

                {/* Search */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Area Code
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={areaCode}
                      onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                      placeholder="Enter 3-digit area code (e.g., 512)"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      maxLength={3}
                    />
                    <button
                      onClick={handleSearchNumbers}
                      disabled={searchingNumbers || areaCode.length !== 3}
                      className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {searchingNumbers ? 'Searching...' : 'Search'}
                    </button>
                  </div>
                </div>

                {/* Available Numbers */}
                {availableNumbers.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900">Available Numbers:</h4>
                    {availableNumbers.map((number) => (
                      <div
                        key={number.phone_number}
                        onClick={() => setSelectedNumber(number.phone_number)}
                        className={`
                          p-4 border-2 rounded-lg cursor-pointer transition-all
                          ${selectedNumber === number.phone_number
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300'
                          }
                        `}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-gray-900">{number.phone_number}</p>
                              {number.number_type === 'toll_free' && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                                  Toll-Free
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{number.locality || 'Nationwide'}</p>
                            {number.number_type === 'toll_free' && (
                              <p className="text-xs text-green-700 mt-1">
                                💡 Forward your existing business phone to this number
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 3: Stripe Connect - PLACEHOLDER */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Connect Stripe Account</h3>
                <p className="text-gray-600 mb-6">
                  Link your Stripe account to receive payments directly from customers.
                </p>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
                  <p className="text-blue-900 font-medium mb-4">Stripe Connection Status:</p>
                  <div className="flex items-center gap-3">
                    {stripeAccountStatus === 'verified' && (
                      <>
                        <CheckCircle className="w-6 h-6 text-green-600" />
                        <span className="text-green-700 font-semibold">Verified</span>
                      </>
                    )}
                    {stripeAccountStatus === 'pending' && (
                      <>
                        <Clock className="w-6 h-6 text-orange-600" />
                        <span className="text-orange-700 font-semibold">Pending Verification</span>
                      </>
                    )}
                    {stripeAccountStatus === 'not_started' && (
                      <>
                        <CreditCard className="w-6 h-6 text-gray-600" />
                        <span className="text-gray-700 font-semibold">Not Connected</span>
                      </>
                    )}
                  </div>
                </div>

                {stripeAccountStatus === 'not_started' && (
                  <button
                    onClick={handleCreateStripeAccount}
                    disabled={loading}
                    className="w-full px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                  >
                    {loading ? 'Creating Account...' : 'Connect Stripe Account'}
                  </button>
                )}

                {(stripeAccountStatus === 'pending' || stripeAccountStatus === 'verified') && (
                  <button
                    onClick={handleCheckStripeStatus}
                    disabled={checkingStripeStatus}
                    className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {checkingStripeStatus ? 'Checking...' : 'Check Verification Status'}
                  </button>
                )}

                <p className="text-sm text-gray-500 mt-4">
                  Note: Stripe verification typically takes 1-2 business days.
                </p>
              </motion.div>
            )}

            {/* Step 4: Preferences - PLACEHOLDER */}
            {currentStep === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Configure Preferences</h3>
                <p className="text-gray-600 mb-6">
                  Customize your Virtual Host settings.
                </p>

                <div className="space-y-6">
                  {/* Welcome Greeting */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Welcome Greeting
                    </label>
                    <textarea
                      value={preferences.welcome_greeting}
                      onChange={(e) => setPreferences({ ...preferences, welcome_greeting: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Thank you for calling! How can I help you today?"
                    />
                  </div>

                  {/* Wait Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Estimated Wait Time (minutes)
                    </label>
                    <input
                      type="number"
                      value={preferences.wait_time_minutes}
                      onChange={(e) => setPreferences({ ...preferences, wait_time_minutes: parseInt(e.target.value) || 15 })}
                      min={5}
                      max={60}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  {/* Human Contact */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Human Contact Phone</label>
                      <input
                        type="tel"
                        value={preferences.human_contact_phone}
                        onChange={(e) => setPreferences({ ...preferences, human_contact_phone: e.target.value })}
                        placeholder="(512) 555-1234"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Human Contact Email</label>
                      <input
                        type="email"
                        value={preferences.human_contact_email}
                        onChange={(e) => setPreferences({ ...preferences, human_contact_email: e.target.value })}
                        placeholder="contact@restaurant.com"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Service Features */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Additional Services
                    </label>
                    <div className="space-y-3">
                      <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                        <input
                          type="checkbox"
                          checked={preferences.enable_reservations}
                          onChange={(e) => setPreferences({ ...preferences, enable_reservations: e.target.checked })}
                          className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                        />
                        <span className="text-gray-900 font-medium">Accept Reservations</span>
                      </label>

                      <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                        <input
                          type="checkbox"
                          checked={preferences.enable_catering}
                          onChange={(e) => setPreferences({ ...preferences, enable_catering: e.target.checked })}
                          className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                        />
                        <span className="text-gray-900 font-medium">Offer Catering</span>
                      </label>

                      <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                        <input
                          type="checkbox"
                          checked={preferences.enable_events}
                          onChange={(e) => setPreferences({ ...preferences, enable_events: e.target.checked })}
                          className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                        />
                        <span className="text-gray-900 font-medium">Host Private Events</span>
                      </label>
                    </div>
                  </div>

                  <button
                    onClick={handleSavePreferences}
                    disabled={loading}
                    className="w-full px-6 py-3 bg-[#FDB913] hover:bg-[#e5a711] text-black rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Saving...' : 'Save Preferences'}
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 5: Finish */}
            {currentStep === 5 && (
              <motion.div
                key="step5"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="text-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.5 }}
                  className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <CheckCircle className="w-16 h-16 text-green-600" />
                </motion.div>

                <h3 className="text-3xl font-bold text-gray-900 mb-4">You're All Set!</h3>
                <p className="text-gray-600 mb-8 text-lg">
                  Your Virtual Host is configured and ready to go live.
                </p>

                <div className="bg-purple-50 rounded-xl p-6 mb-8 text-left">
                  <h4 className="font-semibold text-gray-900 mb-4">Setup Summary:</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-gray-700">Phone number provisioned: {selectedNumber || 'Complete'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-gray-700">Stripe account: {stripeAccountStatus === 'verified' ? 'Verified' : 'Pending'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-gray-700">Preferences configured</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleFinishSetup}
                  disabled={loading}
                  className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Activating...' : 'Activate Virtual Host'}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer Navigation */}
        {currentStep !== 5 && (
          <div className="px-8 py-6 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <button
              onClick={handlePrevStep}
              disabled={currentStep === 1}
              className="flex items-center gap-2 px-6 py-3 text-gray-700 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Previous
            </button>

            <div className="text-sm text-gray-600">
              Step {currentStep} of 5
            </div>

            <button
              onClick={handleNextStep}
              disabled={
                (currentStep === 2 && !selectedNumber) ||
                (currentStep === 3 && stripeAccountStatus !== 'verified')
              }
              className="flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default VirtualHostSetupWizard;
